import { useAuth0 } from "@auth0/auth0-react";
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ACCOUNT_TYPE_LIST, isDebt } from '../constants/ACCOUNT_TYPE'
import { svc_createAccount, svc_createDebtAccount } from "../services/account.service";
import { FFDate, FFSelect, FFText, FFToggle, RCFormProvider, useRCFormContext } from '@jlafica/rcform';
import { fieldNames, initialState } from '../models/AccountModel';
import { useResponseMessages } from "../hooks/useResponseMessage";

const AccountCreate = () => {
    const navigate = useNavigate();
    const { getAccessTokenSilently, user } = useAuth0();
    const { addMessage } = useResponseMessages();
    const [formRef, setFormRef] = useState(null);

    const getAccountData = () => {
        const fields = formRef?.formState.fields;

        return {
            name: fields[fieldNames.name].value,
            institution: fields[fieldNames.institution].value,
            availableBalance: fields[fieldNames.availableBalance].value,
            accountNumber: fields[fieldNames.accountNumber].value,
            status: fields[fieldNames.status].value,
            type: fields[fieldNames.type].value,
            includeBalance: fields[fieldNames.includeBalance].checked ? true : false,
        }
    }

    const getDebtAccountData = () => {
        const fields = formRef?.formState.fields;

        return {
            dueDate: fields[fieldNames.dueDate].value || null,
            balance: fields[fieldNames.balance].value || null,
            lineLimit: fields[fieldNames.lineLimit].value || null,
            minimumPaymentPercentage: parseFloat(fields[fieldNames.minimumPaymentPercentage].value) || null,
            minimumPaymentLowBalance: fields[fieldNames.minimumPaymentLowBalance].value || null,
            fixedPayment: fields[fieldNames.fixedPayment].value || null,
            introRateEndDate: fields[fieldNames.introRateEndDate].value || null,
            apr: parseFloat(fields[fieldNames.apr].value) || null,
            introApr: parseFloat(fields[fieldNames.introApr].value) || null,
        }
    }

    const calculateAvailableBalance = () => {
        const fields = formRef?.formState.fields;

        const calculatedAvailableBalance = isDebt(fields[fieldNames.type].value)
            ? fields[fieldNames.lineLimit].value - fields[fieldNames.balance].value
            : fields[fieldNames.availableBalance].value

        return calculatedAvailableBalance
    }

    const resetFields = () => {
        formRef?.resetForm();
    }

    const createDebtAccount = async (accountId, accessToken) => {
        const debtAccountData = {
            ...getDebtAccountData(),
            accountId
        }

        const { error } = await svc_createDebtAccount(accessToken, user.sub, debtAccountData);

        if (error) {
            console.error("Error creating debt account: ", error);
            addMessage({
                error: error.message
            })
        } else {
            addMessage({
                data: 'Account created successfully'
            })

            resetFields()
            navigate(`/account/${accountId}`);
        }
    }

    const submitAccount = async (e) => {
        e.preventDefault()
        const accessToken = await getAccessTokenSilently();
        const accountData = {
            ...getAccountData(),
            availableBalance: calculateAvailableBalance()
        }
        const { data, error } = await svc_createAccount(accessToken, user.sub, accountData);

        if (error) {
            console.error("Error creating account: ", error);
            addMessage({
                error: error.message
            })
        } else {
            if (data.insertId && isDebt(formRef?.formState.fields[fieldNames.type].value)) {
                createDebtAccount(data.insertId, accessToken);
            } else {
                addMessage({
                    data: 'Account created successfully'
                })
                navigate(`/account/${data.insertId}`);
            }
        }
    }

    return (
        <>
            <h1 className="text-center">Add Account</h1>

            <div className="standardFormWrap">
                <RCFormProvider
                    onSubmit={submitAccount}
                    className={`formStandard ${!formRef?.formState?.isValid ? 'errors' : ''}`}
                    initialFieldState={initialState}
                    setFormRef={setFormRef}
                    id="uploadAccountForm"
                >
                    <FormContent
                        fieldNames={fieldNames}
                        calculateAvailableBalance={calculateAvailableBalance}
                    />
                </RCFormProvider>
            </div>
        </>
    )
}

const FormContent = ({
    fieldNames,
    calculateAvailableBalance
}) => {
    const formContext = useRCFormContext();

    return (
        <>
            <FFToggle
                fieldName={fieldNames.includeBalance}
                label={"Include Available Balance in Budget Funds"}
                wrapperModifierClass={"formField"}
                buttonOffValue={"No"}
                buttonOnValue={"Yes"}
                description={formContext?.formState.fields[fieldNames.includeBalance].checked && <p>By selecting "Yes", this account's available balance will be included in the starting balance of your budget forecast.</p>}
            />

            <FFText
                fieldName={fieldNames.name}
                label={"Name*"}
                wrapperModifierClass={"formField"}
                required={true}
            />

            <FFText
                fieldName={fieldNames.institution}
                label={"Institution"}
                wrapperModifierClass={"formField"}
            />

            <FFText
                fieldName={fieldNames.accountNumber}
                label={"Account Number"}
                wrapperModifierClass={"formField"}
            />

            <FFSelect
                fieldName={fieldNames.status}
                label={"Status"}
                wrapperModifierClass={"formField"}
                options={[
                    { value: "Open", label: "Open" },
                    { value: "Closed", label: "Closed" }
                ]}
            />

            <FFSelect
                fieldName={fieldNames.type}
                label={"Type"}
                wrapperModifierClass={"formField"}
                initialValue={ACCOUNT_TYPE_LIST[0]}
                options={ACCOUNT_TYPE_LIST.map((type) => ({
                    value: type,
                    label: type
                }))}
            />

            {isDebt(formContext?.formState.fields[fieldNames.type].value) && (
                <div className="formField">
                    <label>Available Balance | Calculated (Limit - Balance)</label>
                    <div className="mock-disabled-input">
                        {calculateAvailableBalance()}
                    </div>
                </div>
            )}

            <FFText
                type="number"
                fieldName={fieldNames.availableBalance}
                label={"Available Balance"}
                displayCondition={!isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
                additionalProps={{
                    step: "any",
                }}
            />

            {isDebt(formContext?.formState.fields[fieldNames.type].value) && (
                <h3 className="spc-m-t-1">
                    Debt Info
                </h3>
            )}

            <FFText
                type="number"
                fieldName={fieldNames.balance}
                label={"Balance*"}
                displayCondition={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
                required={isDebt(formContext?.formState.fields[fieldNames.type].value)}
            />

            <FFText
                type="number"
                fieldName={fieldNames.lineLimit}
                label={"Limit*"}
                displayCondition={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
                required={isDebt(formContext?.formState.fields[fieldNames.type].value)}
            />

            <FFText
                type="number"
                fieldName={fieldNames.minimumPaymentPercentage}
                label={`Minimum Payment Percentage* ${Boolean(formContext?.formState.fields[fieldNames.minimumPaymentPercentage].value) ? '[' + parseFloat(formContext?.formState.fields[fieldNames.minimumPaymentPercentage].value * 100).toFixed(2) + "%]" : ''}`}
                displayCondition={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
                required={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                additionalProps={{
                    step: ".01",
                    min: "0",
                    max: "1"
                }}
            />

            <FFText
                type="number"
                fieldName={fieldNames.minimumPaymentLowBalance}
                label={"Minimum Payment for Low Balance*"}
                displayCondition={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
                required={isDebt(formContext?.formState.fields[fieldNames.type].value)}
            />

            <FFText
                type="number"
                fieldName={fieldNames.fixedPayment}
                label={"Fixed Payment"}
                displayCondition={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
            />

            <FFDate
                fieldName={fieldNames.introRateEndDate}
                label={"Intro Rate End Date"}
                displayCondition={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
            />

            <FFText
                type="number"
                fieldName={fieldNames.apr}
                label={`APR* ${Boolean(formContext?.formState.fields[fieldNames.apr].value) ? '[' + parseFloat(formContext?.formState.fields[fieldNames.apr].value * 100).toFixed(2) + "%]" : ''}`}
                displayCondition={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
                required={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                additionalProps={{
                    step: ".01",
                    min: "0",
                    max: "1"
                }}
            />

            <FFText
                type="number"
                fieldName={fieldNames.introApr}
                label={`Intro APR`}
                displayCondition={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
                additionalProps={{
                    step: ".01",
                    min: "0",
                    max: "1"
                }}
            />

            <FFText
                type="number"
                fieldName={fieldNames.dueDate}
                label={"Due Date*"}
                displayCondition={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                wrapperModifierClass={`formField`}
                required={isDebt(formContext?.formState.fields[fieldNames.type].value)}
                additionalProps={{
                    min: "1",
                    max: "28",
                    step: "1"
                }}
            />

            <div className="formField">
                <button type="submit" className="button button-green">
                    Create Account
                </button>
            </div>
        </>
    )
}

export default AccountCreate
